import React, {useState, useCallback, useMemo, useEffect} from 'react'
import {UIGameActionBoxBase, UIGameActionBoxBaseProps} from '../../UIGameActionBoxBase'
import {CardEnum, CardStateUnordered, CardTypeEnum} from '@colonist/game-states'
import {UIGameCardInventory} from '../../../UIGameCardInventory/UIGameCardInventory'
import {TranslationObject} from 'packages/i18n/src'
import styles from './UIGameActionBoxPickCard.module.scss'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {Avatar} from '../../../Avatar/Avatar'
import {CARD_TIP_TITLE, CARD_POPUP_BODY, cardEnumsToCardState} from '../../../../utils/cards'
import {T} from '../../../T/T'
import {CardDataController} from '@colonist/game-card-types'
import {ArrayUtils} from '@colonist/utils'
import clsx from 'clsx'
import {useGameStore} from '../../../../store/GameStoreContext'

type CardSource = 'selectedCards' | 'cardsToSelect' | 'hand'

function getRemainingCardsToSelect(initialCardsToSelect: CardEnum[],
                                   selectedCards: CardEnum[],
                                   removeCardFromSelectionOnClick: boolean): CardEnum[] {
    if(!removeCardFromSelectionOnClick) return initialCardsToSelect

    const remainingCardsToSelect: CardEnum[] = []
    const selectedCardCounts = cardEnumsToCardState(selectedCards)

    for(const card of initialCardsToSelect) {
        const selectedCount = selectedCardCounts[card] ?? 0
        if(selectedCount > 0) {
            selectedCardCounts[card] = selectedCount - 1
        } else {
            remainingCardsToSelect.push(card)
        }
    }

    return remainingCardsToSelect
}

function getIsEnoughSelected(selectedCardsLength: number,
                             amountOfCardsToSelect: number,
                             isSelectFromHand: boolean,
                             remainingCardsToSelectLength: number): boolean {
    const selectedEnough = selectedCardsLength === amountOfCardsToSelect
    const selectingFromActionBox = !isSelectFromHand
    const noCardsToSelectLeft = remainingCardsToSelectLength === 0
    const selectedAtLeastOneCard = selectedCardsLength > 0
    const actionBoxSelectionIsEmpty = selectingFromActionBox && noCardsToSelectLeft && selectedAtLeastOneCard

    return selectedEnough || actionBoxSelectionIsEmpty
}

export function getCardsToGreyOut(isSelectFromHand: boolean,
                                  remainingCardsToSelect: CardEnum[],
                                  playerCardState: CardStateUnordered): CardStateUnordered | undefined {
    const noCardsToSelectLeft = remainingCardsToSelect.length === 0
    const selectingFromActionBox = !isSelectFromHand
    if(selectingFromActionBox || noCardsToSelectLeft) return undefined

    const greyOutState: CardStateUnordered = {}
    Object.entries(playerCardState).forEach(([cardKey, count]) => {
        const card = Number(cardKey) as CardEnum
        const shouldGreyOut = !remainingCardsToSelect.includes(card)
        if(shouldGreyOut) greyOutState[card] = count
    })
    return greyOutState
}

function getShouldShowSelectedCardFooterInformation(showSelectedCardFooterInformation: boolean,
                                                    amountOfCardsToSelect: number,
                                                    selectedCards: CardEnum[]): boolean {
    return showSelectedCardFooterInformation &&
        amountOfCardsToSelect === 1 &&
        selectedCards.length === 1
}

function isProgressCard(card: CardEnum): boolean {
    const cardType = CardDataController.getCardTypeFromCardEnum(card)
    return cardType === CardTypeEnum.ProgressTrade ||
           cardType === CardTypeEnum.ProgressPolitics ||
           cardType === CardTypeEnum.ProgressScience
}

export interface UIGameActionBoxPickCardProps extends UIGameActionBoxBaseProps {
    amountOfCardsToSelect: number
    initialCardsToSelect?: CardEnum[]
    playerCardState: CardStateUnordered
    isSelectFromHand?: boolean
    colorTitleOnSelect?: boolean
    hideBadgeOfSelectedCards?: boolean
    hideBadgeOfCardsToSelect?: boolean
    removeCardFromSelectionOnClick?: boolean
    showSelectedCardFooterInformation?: boolean
    onCardSelect: (selectedCards: CardEnum[]) => void
    createTextOnSelect?: (title: TranslationObject, selectedCount: number, totalRequired: number) => {
        title: TranslationObject
        body: TranslationObject
    }
    playerValidator?: PlayerValidator
}

export const UIGameActionBoxPickCard = ({
    amountOfCardsToSelect,
    initialCardsToSelect = [],
    playerCardState,
    isSelectFromHand = false,
    colorTitleOnSelect = false,
    hideBadgeOfSelectedCards = false,
    hideBadgeOfCardsToSelect = false,
    removeCardFromSelectionOnClick = false,
    showSelectedCardFooterInformation = false,
    onCardSelect,
    createTextOnSelect,
    playerValidator,
    isConfirmDisabled,
    isPortrait,
    ...baseProps
}: UIGameActionBoxPickCardProps) => {
    const [selectedCards, setSelectedCards] = useState<CardEnum[]>([])
    const setOnSelectCardForActionBox = useGameStore((state) => state.gameCardInventory.setOnSelectCardForActionBox)
    const setSelectedCardsInInventory = useGameStore((state) => state.gameCardInventory.setSelectedCards)
    const remainingCardsToSelect = useMemo(() => getRemainingCardsToSelect(initialCardsToSelect, selectedCards, removeCardFromSelectionOnClick),
        [initialCardsToSelect, selectedCards, removeCardFromSelectionOnClick])

    const canAddMoreCards = selectedCards.length < amountOfCardsToSelect
    const isEnoughSelected = getIsEnoughSelected(selectedCards.length,
        amountOfCardsToSelect,
        isSelectFromHand,
        remainingCardsToSelect.length)
    const shouldDisableConfirm = isConfirmDisabled ? isConfirmDisabled : !isEnoughSelected

    const cardsToGreyOut = useMemo(() => getCardsToGreyOut(isSelectFromHand, remainingCardsToSelect, playerCardState),
        [isSelectFromHand, remainingCardsToSelect, playerCardState])

    const setCardsToGreyOut = useGameStore((state) => state.gameCardInventory.setCardsToGreyOut)
    useEffect(() => {
        setCardsToGreyOut(cardsToGreyOut)
    }, [cardsToGreyOut])

    const progressCardsToScaleOnHover = useMemo(() => {
        const actionBoxCards = [...remainingCardsToSelect, ...selectedCards]
        return actionBoxCards.filter(isProgressCard)
    }, [remainingCardsToSelect, selectedCards])

    const handleCardClick = useCallback((card: CardEnum, source: CardSource) => {
        const selectedGreyedOutInventoryCard = source === 'hand' && cardsToGreyOut && cardsToGreyOut[card] != undefined
        if(selectedGreyedOutInventoryCard) return

        const shouldNotUpdateSelection = source === 'hand' && !isSelectFromHand
        if(shouldNotUpdateSelection) return

        const shouldRemoveCardFromSelection = source === 'selectedCards'
        if(shouldRemoveCardFromSelection) {
            setSelectedCards((prev) => {
                const newSelection = ArrayUtils.removeOneElement(prev, card)
                if(newSelection !== prev) onCardSelect(newSelection)
                if(isSelectFromHand) {
                    setTimeout(() => setSelectedCardsInInventory([...newSelection]), 0)
                }
                return newSelection
            })
            return
        }

        const shouldReplaceCard = amountOfCardsToSelect === 1 && !isSelectFromHand
        const shouldAddOrReplaceCard = canAddMoreCards || shouldReplaceCard
        if(shouldAddOrReplaceCard) {
            setSelectedCards((prev) => {
                const newSelection = shouldReplaceCard ? [card] : [...prev, card]
                onCardSelect(newSelection)
                if(isSelectFromHand) {
                    setTimeout(() => setSelectedCardsInInventory([...newSelection]), 0)
                }
                return newSelection
            })
        }
    }, [canAddMoreCards, amountOfCardsToSelect, isSelectFromHand, onCardSelect, cardsToGreyOut])

    useEffect(() => () => {
        setSelectedCardsInInventory([])
    }, [])

    useEffect(() => {
        setOnSelectCardForActionBox((card) => handleCardClick(card, 'hand'))
    }, [handleCardClick])

    const currentText = useMemo(() => {
        if(createTextOnSelect) return createTextOnSelect(baseProps.title, selectedCards.length, amountOfCardsToSelect)
        return {title: baseProps.title, body: baseProps.body}
    }, [createTextOnSelect, selectedCards.length, amountOfCardsToSelect, baseProps.title, baseProps.body])

    const handleConfirm = useCallback(() => {
        if(isEnoughSelected) {
            baseProps.onConfirm()
            setSelectedCardsInInventory([])
        }
    }, [isEnoughSelected, baseProps])

    const validCardsToSelectState = useMemo(() => cardEnumsToCardState(remainingCardsToSelect),
        [remainingCardsToSelect])

    const selectedCardsState = useMemo(() => cardEnumsToCardState(selectedCards),
        [selectedCards])

    const cardSelectionContent = (
        <div className={clsx(styles.cardSelectionContainer, {
            [styles.hasScalableCards]: progressCardsToScaleOnHover.length > 0,
        })}>
            <UIGameCardInventory
                cardState={isSelectFromHand ? selectedCardsState : validCardsToSelectState}
                onClickCard={(card) => handleCardClick(card, isSelectFromHand ? 'selectedCards' : 'cardsToSelect')}
                forceShowCardBadges={!hideBadgeOfCardsToSelect}
                showTipTitle={false}
                showTipBody={false}
                className={styles.cardSelection}
                cardsToScaleOnHover={progressCardsToScaleOnHover}
            />
            {playerValidator && (
                <div className={styles.playerContainer}>
                    <Avatar
                        className={styles.playerAvatar}
                        color={playerValidator.color}
                        image={playerValidator.displayIcon}
                        profilePictureUrl={playerValidator.profilePictureUrl}
                    />
                    <div className={styles.playerName}>
                        {playerValidator.username}
                    </div>
                </div>
            )}
        </div>
    )

    const shouldShowSelectedCardFooterInformation = getShouldShowSelectedCardFooterInformation(showSelectedCardFooterInformation,
        amountOfCardsToSelect,
        selectedCards)

    const footerContent = !isSelectFromHand ? (
        <div className={styles.selectedCardsFooter}>
            <UIGameCardInventory
                cardState={selectedCardsState}
                onClickCard={(card) => handleCardClick(card, 'selectedCards')}
                showTipTitle={false}
                showTipBody={false}
                className={styles.selectedCardsInventory}
                forceShowCardBadges={!hideBadgeOfSelectedCards}
                cardsToScaleOnHover={progressCardsToScaleOnHover}
            />
            {shouldShowSelectedCardFooterInformation && (
                <div className={styles.footerCardInfo}>
                    <div className={styles.cardTitle}>
                        <T id={CARD_TIP_TITLE[selectedCards[0]]} />
                    </div>
                    <div className={styles.cardBody}>
                        <T id={CARD_POPUP_BODY[selectedCards[0]]} />
                    </div>
                </div>
            )}
        </div>
    ) : <div className={styles.emptyFooter}></div>

    const titleColor = colorTitleOnSelect ? (isEnoughSelected ? styles.greenTitle : styles.redTitle) : undefined

    return (
        <div className={styles.actionBoxWrapper}>
            <UIGameActionBoxBase
                {...baseProps}
                {...(isPortrait !== undefined && {isPortrait: isPortrait})}
                title={currentText.title}
                body={currentText.body}
                {...(titleColor !== undefined && {titleClassName: titleColor})}
                footerContent={footerContent}
                isConfirmDisabled={shouldDisableConfirm}
                onConfirm={handleConfirm}
                className={styles.hasCardInventory}
            >
                {cardSelectionContent}
            </UIGameActionBoxBase>
        </div>
    )
}
